import type { AdminPeriodRange, AdminPeriodType } from '@/composables/useAdminPeriod'
import moment from 'moment'

export type ChartGranularity = 'day' | 'week' | 'month'

export type ChartDisplayMode = 'period' | 'cumulative'

export interface AdminDashboardBucket {
  bucketKey: string
  label: string
  count: number
}

export interface AdminDashboardTotals {
  companies: number
  employees: number
  deliveryNotes: number
  aiRequests: number
}

export interface AdminDashboardSeries {
  companies: AdminDashboardBucket[]
  employees: AdminDashboardBucket[]
  deliveryNotes: AdminDashboardBucket[]
  aiRequests: AdminDashboardBucket[]
}

export interface AdminDashboardData {
  totals: AdminDashboardTotals
  series: AdminDashboardSeries
}

export function resolveChartGranularity(
  periodType: AdminPeriodType,
  periodDurationDays: number
): ChartGranularity {
  if (periodType === 'month') {
    return 'day'
  }

  if (periodType === 'quarter') {
    return 'week'
  }

  if (periodType === 'year') {
    return 'month'
  }

  if (periodDurationDays <= 62) {
    return 'day'
  }

  if (periodDurationDays <= 200) {
    return 'week'
  }

  return 'month'
}

function isFutureBucket(bucketKey: string): boolean {
  const bucketStart = moment(bucketKey, 'YYYY-MM-DD').startOf('day')
  return bucketStart.isAfter(moment().startOf('day'))
}

export function applyChartDisplayMode(
  buckets: AdminDashboardBucket[],
  displayMode: ChartDisplayMode
): AdminDashboardBucket[] {
  if (displayMode === 'period') {
    return buckets
  }

  let runningTotal = 0
  return buckets.map((bucket) => {
    if (isFutureBucket(bucket.bucketKey)) {
      return {
        ...bucket,
        count: 0
      }
    }

    runningTotal += bucket.count
    return {
      ...bucket,
      count: runningTotal
    }
  })
}

export function resolveGranularityFromPeriod(periodRange: AdminPeriodRange, periodDurationDays: number): ChartGranularity {
  return resolveChartGranularity(periodRange.periodType, periodDurationDays)
}
